import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Zap, Check, CreditCard, Star, Clock } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export default function PaymentDialog({ open, onClose }) {
  const { user, refreshUser, authHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handlePurchase = async (packageId) => {
    setLoading(true);
    setSelectedPlan(packageId);
    try {
      const response = await axios.post(
        `${API}/payments/checkout`,
        {
          package_id: packageId,
          origin_url: window.location.origin
        },
        { headers: authHeaders() }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Failed to create checkout session");
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const features = [
    "Unlimited AI predictions",
    "AI Hot Picks (20 predicted 50%+ gainers)",
    "Portfolio analytics & tracking",
    "Price alerts",
    "Real-time market data",
    "Priority support"
  ];

  const isSubscribed = user?.subscription_status === "subscribed";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-solid max-w-[95vw] sm:max-w-lg md:max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-white text-xl sm:text-2xl text-center" style={{ fontFamily: 'Unbounded' }}>
            CHOOSE YOUR PLAN
          </DialogTitle>
          <p className="text-center text-gray-400 mt-2 text-sm">
            Unlock all features with any plan
          </p>
        </DialogHeader>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mt-4 sm:mt-6">
          {/* Trial Plan */}
          <Card className="glass-effect border-[#00E0FF]/30 hover:border-[#00E0FF]/50 transition-all duration-300 relative mt-4">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="px-3 py-1 bg-[#00E0FF] text-black rounded-full text-xs font-bold whitespace-nowrap">
                START HERE
              </span>
            </div>
            <div className="p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-5 h-5 text-[#00E0FF]" />
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
                  TRIAL
                </h3>
              </div>
              
              <div className="mb-4">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl sm:text-4xl font-bold text-white mono">$4.99</span>
                  <span className="text-gray-400 text-sm">/1st month</span>
                </div>
                <p className="text-xs text-gray-400">Try all features for 30 days</p>
              </div>

              <div className="space-y-2 mb-4">
                {features.slice(0, 4).map((feature, index) => (
                  <div key={`feature-${feature}`} className="flex items-start gap-2 text-gray-300 text-xs sm:text-sm">
                    <Check className="w-4 h-4 text-[#00E0FF] shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <Button
                data-testid="trial-btn"
                onClick={() => handlePurchase("trial")}
                disabled={loading || isSubscribed}
                className="w-full bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-bold py-4 sm:py-5 text-sm"
              >
                {loading && selectedPlan === "trial" ? "Processing..." : 
                 isSubscribed ? "Already Active" : "Start Trial"}
              </Button>
            </div>
          </Card>

          {/* Monthly Plan */}
          <Card className="glass-effect border-white/10 hover:border-white/20 transition-all duration-300">
            <div className="p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="w-5 h-5 text-[#F59E0B]" />
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
                  MONTHLY
                </h3>
              </div>
              
              <div className="mb-4">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl sm:text-4xl font-bold text-white mono">$9.99</span>
                  <span className="text-gray-400 text-sm">/month</span>
                </div>
                <p className="text-xs text-gray-400">Billed monthly, cancel anytime</p>
              </div>

              <div className="space-y-2 mb-4">
                {features.slice(0, 4).map((feature, index) => (
                  <div key={`feature-${feature}`} className="flex items-start gap-2 text-gray-300 text-xs sm:text-sm">
                    <Check className="w-4 h-4 text-[#F59E0B] shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <Button
                data-testid="monthly-btn"
                onClick={() => handlePurchase("monthly")}
                disabled={loading || isSubscribed}
                variant="outline"
                className="w-full border-[#F59E0B]/50 text-[#F59E0B] hover:bg-[#F59E0B]/10 font-bold py-4 sm:py-5 text-sm"
              >
                {loading && selectedPlan === "monthly" ? "Processing..." : 
                 isSubscribed ? "Already Active" : "Subscribe Monthly"}
              </Button>
            </div>
          </Card>

          {/* Annual Plan */}
          <Card className="glass-effect border-[#00FFA3]/30 hover:border-[#00FFA3]/50 transition-all duration-300 relative mt-4">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2">
              <span className="px-3 py-1 bg-[#00FFA3] text-black rounded-full text-xs font-bold flex items-center gap-1 whitespace-nowrap">
                <Star className="w-3 h-3" />
                BEST VALUE
              </span>
            </div>
            <div className="p-4 sm:p-6">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-[#00FFA3]" />
                <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded' }}>
                  ANNUAL
                </h3>
              </div>
              
              <div className="mb-4">
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-3xl sm:text-4xl font-bold text-white mono">$59.99</span>
                  <span className="text-gray-400 text-sm">/year</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400 line-through">$119.88</span>
                  <span className="px-2 py-0.5 bg-[#00FFA3]/20 text-[#00FFA3] rounded text-xs font-bold">
                    SAVE 50%
                  </span>
                </div>
                <p className="text-xs text-[#00FFA3] mt-1">Only $5/month</p>
              </div>

              <div className="space-y-2 mb-4">
                {features.map((feature, index) => (
                  <div key={`feature-${feature}`} className="flex items-start gap-2 text-gray-300 text-xs sm:text-sm">
                    <Check className="w-4 h-4 text-[#00FFA3] shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <Button
                data-testid="annual-btn"
                onClick={() => handlePurchase("annual")}
                disabled={loading || isSubscribed}
                className="w-full bg-[#00FFA3] text-black hover:bg-[#00FFA3]/90 font-bold py-4 sm:py-5 text-sm shadow-[0_0_20px_rgba(0,255,163,0.3)] hover:shadow-[0_0_30px_rgba(0,255,163,0.5)]"
              >
                {loading && selectedPlan === "annual" ? "Processing..." : 
                 isSubscribed ? "Already Active" : "Get Annual (Save 50%)"}
              </Button>
            </div>
          </Card>
        </div>

        {/* All features list */}
        <div className="mt-4 p-3 sm:p-4 glass-effect rounded-lg border border-white/5">
          <h4 className="text-white font-semibold mb-2 text-center text-sm">All Plans Include:</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {features.map((feature, index) => (
              <div key={`all-${feature}`} className="flex items-start gap-2 text-gray-400 text-xs sm:text-sm">
                <Check className="w-3 h-3 text-[#00E0FF] shrink-0 mt-0.5" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {user && (
          <div className="mt-3 p-3 glass-effect rounded-lg border border-white/5">
            <div className="flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-gray-400 shrink-0" />
              <div>
                <div className="text-xs text-gray-400">Current Status</div>
                <div className="text-white font-semibold text-sm">
                  {isSubscribed ? "Premium Subscriber" : "Free Plan"}
                </div>
              </div>
            </div>
          </div>
        )}

        <p className="text-xs text-gray-500 text-center mt-3">
          Secure payments powered by Stripe. Cancel anytime.
        </p>
      </DialogContent>
    </Dialog>
  );
}
